import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import { hardwareAPI, type Logger, type DeviceActionResult } from '../../services';
import { LOGGER_FREQUENCY_OPTIONS, MODE_OPTIONS, frequencyLabel, modeLabel } from './hardwareDisplay';

interface DialogProps {
  logger: Logger | null;
  onClose: () => void;
  // Called after the backend accepted the change
  onDone: (result: DeviceActionResult) => void;
}

const errorText = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

/** ConXagent only. Sends TempTrack COMM_MODE; the logger switches at its
 *  next check-in, and a wrong mode can cut it off until someone is on site. */
export const ChangeModeDialog: React.FC<DialogProps> = ({ logger, onClose, onDone }) => {
  const { t } = useTranslation();
  const current = (logger?.Mode || '').toUpperCase();
  const [mode, setMode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setMode('');
    setError('');
  }, [logger?.LoggerID]);

  const submit = async () => {
    if (!logger || !mode) return;
    setSaving(true);
    setError('');
    try {
      onDone(await hardwareAPI.changeMode(logger.LoggerID, mode));
    } catch (e: any) {
      setError(errorText(e, t('hardware.modeFailed', 'Mode change failed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!logger} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t('hardware.changeModeTitle', 'Change mode: {{name}}', { name: logger?.HMTechNumber || logger?.SerialNumber || '' })}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('hardware.currentMode', 'Current mode')}: <strong>{modeLabel(logger?.Mode)}</strong>
        </Typography>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          {MODE_OPTIONS.map((o) => (
            <FormControlLabel
              key={o.value}
              value={o.value}
              disabled={o.value === current}
              control={<Radio size="small" />}
              label={
                <span>
                  <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>{o.label}</Typography>
                  <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                    {o.hint}
                  </Typography>
                </span>
              }
              sx={{ alignItems: 'flex-start', mb: 1, '& .MuiRadio-root': { pt: 0.25 } }}
            />
          ))}
        </RadioGroup>
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t(
            'hardware.modeWarning',
            'The logger switches at its next check-in. If the new mode cannot reach ConXedge (for example XBee with no gateway nearby), the logger stays offline until someone changes it on the device.',
          )}
        </Alert>
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t('common.cancel', 'Cancel')}</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={submit}
          disabled={!mode || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('hardware.sendModeChange', 'Send mode change')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Logging frequency for scheduled loggers. Saved on our side; reaches the
 *  logger only when the backend has frequency push switched on. */
export const ChangeFrequencyDialog: React.FC<DialogProps> = ({ logger, onClose, onDone }) => {
  const { t } = useTranslation();
  const [frequency, setFrequency] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const current = logger?.LoggerFrequency || '';
    setFrequency(LOGGER_FREQUENCY_OPTIONS.some((o) => o.value === current) ? current : '');
    setError('');
  }, [logger?.LoggerID, logger?.LoggerFrequency]);

  const submit = async () => {
    if (!logger || !frequency) return;
    setSaving(true);
    setError('');
    try {
      onDone(await hardwareAPI.changeFrequency(logger.LoggerID, frequency));
    } catch (e: any) {
      setError(errorText(e, t('hardware.frequencyFailed', 'Frequency change failed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!logger} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t('hardware.changeFrequencyTitle', 'Logging frequency: {{name}}', { name: logger?.HMTechNumber || logger?.SerialNumber || '' })}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('hardware.currentFrequency', 'Current')}: <strong>{frequencyLabel(logger?.LoggerFrequency || undefined) || '—'}</strong>
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel id="freq-dialog-label">{t('hardware.measurementFrequency', 'Measurement frequency')}</InputLabel>
          <Select
            labelId="freq-dialog-label"
            label={t('hardware.measurementFrequency', 'Measurement frequency')}
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
          >
            {LOGGER_FREQUENCY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {t('hardware.frequencyHint', 'Longer intervals save battery. The logger applies a change at its next check-in.')}
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t('common.cancel', 'Cancel')}</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!frequency || frequency === logger?.LoggerFrequency || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
